import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Smartphone, ChevronLeft, LogOut, Loader2, AlertCircle, 
  CheckCircle, Wifi, Monitor, QrCode, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

/**
 * Staff Device Registration Page
 * Allows staff to register their device for the cafe's systems
 */
export default function StaffDevicePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [staffInfo, setStaffInfo] = useState(null);
  const [registeredDevices, setRegisteredDevices] = useState([]);
  const [deviceName, setDeviceName] = useState('');
  const [registering, setRegistering] = useState(false);

  const token = localStorage.getItem('staff_token');

  useEffect(() => {
    if (!token) {
      navigate('/staff/login');
      return;
    }
    fetchStaffInfo();
    fetchDevices();
  }, [token, navigate]);

  const fetchStaffInfo = async () => {
    try {
      const res = await api.get(`/staff-portal/profile?token=${token}`);
      setStaffInfo(res.data);
    } catch (err) {
      console.error('Failed to fetch staff info:', err);
    }
  };

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/staff-portal/my-devices?token=${token}`);
      setRegisteredDevices(res.data.devices || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('staff_token');
        navigate('/staff/login');
      }
      console.error('Failed to fetch devices:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterDevice = async () => {
    if (!deviceName.trim()) {
      toast.error('Please enter a device name');
      return;
    }

    setRegistering(true);
    try {
      // Get device info
      const deviceInfo = {
        name: deviceName,
        user_agent: navigator.userAgent,
        platform: navigator.platform,
        screen_width: window.screen.width,
        screen_height: window.screen.height
      };

      await api.post(`/staff-portal/register-device?token=${token}`, deviceInfo);
      toast.success('Device registered successfully!');
      setDeviceName('');
      fetchDevices();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to register device');
    } finally {
      setRegistering(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('staff_token');
    localStorage.removeItem('staff_info');
    toast.success('Logged out');
    navigate('/staff/login');
  };

  const getDeviceIcon = (device) => {
    const ua = (device.user_agent || '').toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return <Smartphone className="w-5 h-5" />;
    }
    return <Monitor className="w-5 h-5" />;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/staff/profile" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
              <ChevronLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="font-bold text-gray-900 dark:text-white">My Device</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">{staffInfo?.name || 'Staff'}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* WiFi Instructions */}
        <div className="bg-teal-50 dark:bg-teal-900/20 rounded-xl border border-teal-200 dark:border-teal-800 p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
              <Wifi className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-teal-900 dark:text-teal-300">Step 1: Connect to Staff WiFi</h2>
              <p className="text-teal-700 dark:text-teal-400 mt-1">
                Connect your device to the staff network to access cafe systems.
              </p>
              <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  <strong>Network:</strong> BC-Namaka-S
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  <strong>Password:</strong> Ask your manager
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Register Device */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Step 2: Register This Device</h2>
              <p className="text-gray-500 dark:text-gray-400 mt-1">
                Register your phone or tablet to use the Time Clock and other staff features.
              </p>
              <div className="mt-4 flex gap-3">
                <Input
                  type="text"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  placeholder="e.g., My iPhone, Work Tablet"
                  className="flex-1 bg-gray-50 dark:bg-gray-700"
                />
                <Button 
                  onClick={handleRegisterDevice}
                  disabled={registering || !deviceName.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {registering ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Register'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Registered Devices */}
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Your Registered Devices</h2>
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : registeredDevices.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 text-center">
            <Smartphone className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No devices registered yet</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              Register this device above to get started
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {registeredDevices.map((device, index) => (
              <div 
                key={device.id || index}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center text-gray-600 dark:text-gray-300">
                      {getDeviceIcon(device)}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{device.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Registered {device.registered_at ? new Date(device.registered_at).toLocaleDateString() : 'Recently'}
                      </p>
                    </div>
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Help Section */}
        <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
            <div>
              <p className="font-medium text-amber-900 dark:text-amber-300">Need Help?</p>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                If you&apos;re having trouble connecting or registering your device, please speak with your manager or the on-duty supervisor.
              </p>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-6 grid grid-cols-2 gap-4">
          <Link 
            to="/staff/clock"
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center hover:border-teal-500 transition-colors"
          >
            <RefreshCw className="w-8 h-8 text-teal-600 mx-auto mb-2" />
            <p className="font-medium text-gray-900 dark:text-white">Time Clock</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Clock in/out</p>
          </Link>
          <Link 
            to="/staff/profile"
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center hover:border-teal-500 transition-colors"
          >
            <QrCode className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="font-medium text-gray-900 dark:text-white">My Profile</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Account settings</p>
          </Link>
        </div>
      </main>
    </div>
  );
}
